/**
 * useNotifications Hook
 * Manages notification state with real-time SSE updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  Notification,
  NotificationPagination
} from '../services/api/notifications';
import { notificationsApi } from '../services/api/notifications';

interface UseNotificationsOptions {
  /** Auto-fetch notifications on mount */
  autoFetch?: boolean;
  /** Poll interval in ms (0 to disable) */
  pollInterval?: number;
  /** Enable SSE real-time updates */
  enableSSE?: boolean;
  /** Initial limit for notifications */
  limit?: number;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  pagination: NotificationPagination | null;
  
  // Actions
  fetchNotifications: (page?: number) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
  
  // For SSE integration
  addNotification: (notification: Notification) => void;
  updateUnreadCount: (count: number) => void;
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const {
    autoFetch = true,
    pollInterval = 0,
    limit = 20
  } = options;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<NotificationPagination | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await notificationsApi.getNotifications({ page, limit });
      
      if (response.success) {
        setNotifications(response.data);
        setPagination(response.pagination);
      } else {
        setError('Failed to fetch notifications');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  /**
   * Fetch unread count for badge
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await notificationsApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
    }
  }, []);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      
      // Decrement unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      throw err;
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      throw err;
    }
  }, []);

  /**
   * Dismiss a notification
   */
  const dismiss = useCallback(async (id: string) => {
    try {
      await notificationsApi.dismissNotification(id);
      
      // Remove from local state
      const notification = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      
      // Decrement unread count if it was unread
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      throw err;
    }
  }, [notifications]);

  /**
   * Refresh both notifications and unread count
   */
  const refresh = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount()
    ]);
  }, [fetchNotifications, fetchUnreadCount]);

  /**
   * Add a notification (for SSE updates)
   */
  const addNotification = useCallback((notification: Notification) => {
    setNotifications(prev => {
      // Avoid duplicates
      if (prev.some(n => n.id === notification.id)) {
        return prev;
      }
      return [notification, ...prev];
    });
    
    // Increment unread count if notification is unread
    if (!notification.read) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  /**
   * Update unread count (for SSE updates)
   */
  const updateUnreadCount = useCallback((count: number) => {
    setUnreadCount(count);
  }, []);

  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      refresh();
    }
  }, [autoFetch, refresh]);

  // Polling
  useEffect(() => {
    if (pollInterval > 0) {
      pollIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
      }, pollInterval);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [pollInterval, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    pagination,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    dismiss,
    refresh,
    addNotification,
    updateUnreadCount
  };
}

export default useNotifications;
